import { useState, useRef, useEffect } from "react";
import { MdEdit } from "react-icons/md";

const TextStyleTools = ({
    selectedTextStyle,
    setSelectedTextStyle,
    onShowDurationEditor,
    elapsedTime,
    recordedVideoUrl,
    textStartTime,
    textEndTime,
    setTextStartTime,
    setTextEndTime
}) => {
    const [isInlineDurationEditor, setIsInlineDurationEditor] = useState(false);
    const [isDraggingStart, setIsDraggingStart] = useState(false);
    const [isDraggingEnd, setIsDraggingEnd] = useState(false);
    const timelineRef = useRef(null);
    const videoScrollRef = useRef(null);
    const totalDuration = elapsedTime || 30;
    const textStyles = [
        {
            id: 0,
            name: 'Arial Bold',
            style: { fontSize: '14px', fontWeight: 'bold', color: '#000000', fontFamily: 'Arial, sans-serif' },
            preview: 'Sample Text'
        },
        {
            id: 1,
            name: 'Georgia',
            style: { fontSize: '14px', fontWeight: '600', color: '#000000', fontFamily: 'Georgia, serif' },
            preview: 'Sample Text'
        },
        {
            id: 2,
            name: 'Helvetica',
            style: { fontSize: '14px', fontWeight: 'bold', color: '#000000', fontFamily: 'Helvetica, Arial, sans-serif' },
            preview: 'Sample Text'
        },
        {
            id: 3,
            name: 'Times Roman',
            style: { fontSize: '14px', fontWeight: '500', color: '#000000', fontFamily: 'Times New Roman, serif' },
            preview: 'Sample Text'
        },
        {
            id: 4,
            name: 'Verdana',
            style: { fontSize: '14px', fontWeight: 'bold', color: '#000000', fontFamily: 'Verdana, sans-serif' },
            preview: 'Sample Text'
        },
        {
            id: 5,
            name: 'Courier',
            style: { fontSize: '14px', fontWeight: '600', color: '#000000', fontFamily: 'Courier New, monospace' },
            preview: 'Sample Text'
        },
        {
            id: 6,
            name: 'Impact',
            style: { fontSize: '14px', fontWeight: '900', color: '#000000', fontFamily: 'Impact, Arial Black, sans-serif' },
            preview: 'Sample Text'
        },
        {
            id: 7,
            name: 'Comic Sans',
            style: { fontSize: '14px', fontWeight: '600', color: '#000000', fontFamily: 'Comic Sans MS, cursive' },
            preview: 'Sample Text'
        },
        {
            id: 8,
            name: 'Trebuchet',
            style: { fontSize: '14px', fontWeight: '600', color: '#000000', fontFamily: 'Trebuchet MS, sans-serif' },
            preview: 'Sample Text'
        },
        {
            id: 9,
            name: 'Palatino',
            style: { fontSize: '14px', fontWeight: '500', color: '#000000', fontFamily: 'Palatino, serif' },
            preview: 'Sample Text'
        }
    ];

    const handleTrimDragStart = (event, isLeft) => {
        event.preventDefault();
        if (isLeft) {
            setIsDraggingStart(true);
        } else {
            setIsDraggingEnd(true);
        }
    };

    const handleMouseUp = () => {
        setIsDraggingStart(false);
        setIsDraggingEnd(false);
    };

    useEffect(() => {
        if (isDraggingStart || isDraggingEnd) {
            const handleMouseMove = (e) => {
                if (!videoScrollRef.current) return;
                
                const container = videoScrollRef.current.parentElement; // Get the container with overflow-x-auto
                const containerRect = container.getBoundingClientRect();
                const thumbWidth = 30;
                const relativeX = Math.max(0, e.clientX - containerRect.left);
                
                // Convert to seconds based on position (accounting for scroll position)
                const scrollLeft = container.scrollLeft;
                const absoluteX = relativeX + scrollLeft;
                const pixelsPerSecond = thumbWidth;
                const newSecond = Math.min(
                    Math.max(0, Math.floor(absoluteX / pixelsPerSecond)),
                    totalDuration - 1
                );

                if (isDraggingStart) {
                    const newStart = Math.min(newSecond, textEndTime - 1);
                    setTextStartTime(newStart);
                } else if (isDraggingEnd) {
                    const newEnd = Math.max(newSecond + 1, textStartTime + 1);
                    setTextEndTime(newEnd);
                }
            };
            
            const handleMouseUp = () => {
                setIsDraggingStart(false);
                setIsDraggingEnd(false);
            };

            // Add event listeners to window to handle mouse movement outside the component
            window.addEventListener('mousemove', handleMouseMove);
            window.addEventListener('mouseup', handleMouseUp);

            // Clean up event listeners when dragging stops or component unmounts
            return () => {
                window.removeEventListener('mousemove', handleMouseMove);
                window.removeEventListener('mouseup', handleMouseUp);
            };
        }
    }, [isDraggingStart, isDraggingEnd, textStartTime, textEndTime, totalDuration]);

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const getStartPosition = () => (textStartTime / totalDuration) * 100;
    const getEndPosition = () => (textEndTime / totalDuration) * 100;
    const getDurationWidth = () => getEndPosition() - getStartPosition();
    const getTextDurationInSeconds = () => Math.round(textEndTime - textStartTime);

    const handleEditDurationClick = () => {
        setIsInlineDurationEditor(true);
    };

    const handleDurationEditorDone = () => {
        setIsInlineDurationEditor(false);
    };

    return (
        <div className="absolute bottom-0 w-full flex flex-col items-center justify-center mb-3">
            {!isInlineDurationEditor ? (
                <>
                    {/* Edit Duration Button */}
                    <div className="w-full px-4 pb-1">
                        <button
                            onClick={handleEditDurationClick}
                            className="flex items-center gap-x-2 text-gray-900"
                        >
                            <MdEdit className="text-[18px]" />
                            <span className="text-[14px] font-medium">Edit Duration</span>
                        </button>
                    </div>

                    {/* Text Style Options */}
                    <div className="w-full px-4">
                        <div className="overflow-x-scroll">
                            <div className="flex gap-2" style={{ width: 'max-content' }}>
                                {textStyles.map((style) => (
                                    <button
                                        key={style.id}
                                        onClick={() => setSelectedTextStyle(style.id)}
                                        className={`px-2 py-1 rounded-lg border-2 transition-all duration-200 w-24 flex-shrink-0 ${selectedTextStyle === style.id
                                                ? 'border-[#000000] bg-[#BDE6FF] text-black'
                                                : 'bg-[#BDE6FF] text-black'
                                            }`}
                                    >
                                        <div style={style.style} className="text-[8px] text-center truncate">
                                            {style.name}
                                        </div>
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                </>
            ) : (
                <>
                    {/* Dynamic Time Display */}
                    <div className="w-full px-4 pb-2 flex justify-start">
                        <div className="text-black text-[14px]">
                            Text lasts for {getTextDurationInSeconds()} sec
                        </div>
                    </div>

                    {/* Video Preview Scroller with UploadVideo-style Draggers */}
                    <div className="w-full px-4">
                        <div className="relative overflow-x-auto">
                            <div 
                                ref={videoScrollRef}
                                className="flex flex-row min-w-max relative"
                                style={{ scrollBehavior: 'smooth' }}
                            >
                                {recordedVideoUrl ? (
                                    Array.from({ length: Math.ceil(totalDuration) }, (_, index) => (
                                        <div key={index} className="w-[30px] h-[48px] bg-gray-300 border-r border-gray-400 overflow-hidden flex-shrink-0">
                                            <video
                                                muted
                                                src={recordedVideoUrl}
                                                disablePictureInPicture
                                                className="object-cover w-full h-full"
                                                onLoadedData={(e) => {
                                                    e.target.currentTime = index;
                                                }}
                                            />
                                        </div>
                                    ))
                                ) : (
                                    <div className="w-full h-[48px] bg-gray-300 flex items-center justify-center">
                                        <p className="text-gray-600 text-xs">No recording available</p>
                                    </div>
                                )}

                                {/* UploadVideo-style Drag Handles */}
                                {recordedVideoUrl && (
                                    <>
                                        {/* Left drag handle */}
                                        <div
                                            className="absolute top-0 h-[48px] w-1 bg-blue-500 cursor-ew-resize"
                                            style={{
                                                left: `${(textStartTime / totalDuration) * (totalDuration * 30)}px`,
                                                zIndex: 10
                                            }}
                                            onMouseDown={(e) => handleTrimDragStart(e, true)}
                                        >
                                            <div className="absolute top-[20px] left-[0px] w-[10px] h-[10px] bg-blue-500 rounded-full" />
                                        </div>

                                        {/* Right drag handle */}
                                        <div
                                            className="absolute top-0 h-[48px] w-1 bg-blue-500 cursor-ew-resize"
                                            style={{
                                                left: `${(textEndTime / totalDuration) * (totalDuration * 30)}px`,
                                                zIndex: 10
                                            }}
                                            onMouseDown={(e) => handleTrimDragStart(e, false)}
                                        >
                                            <div className="absolute top-[20px] left-[-7px] w-[10px] h-[10px] bg-blue-500 rounded-full" />
                                        </div>

                                        {/* Duration indicator overlay */}
                                        <div
                                            className="absolute top-0 h-[48px] bg-blue-500 bg-opacity-20 pointer-events-none"
                                            style={{
                                                left: `${(textStartTime / totalDuration) * (totalDuration * 30)}px`,
                                                width: `${((textEndTime - textStartTime) / totalDuration) * (totalDuration * 30)}px`
                                            }}
                                        ></div>
                                    </>
                                )}
                            </div>
                        </div>
                        
                        {/* Done button */}
                        {/* <div className="flex justify-center mt-3">
                            <button
                                onClick={handleDurationEditorDone}
                                className="px-6 py-2 bg-[#38B6FF] text-white rounded-lg hover:bg-[#2da5ef] transition-colors text-[14px] font-medium"
                            >
                                Done
                            </button>
                        </div> */}
                    </div>
                </>
            )}
        </div>
    );
};

export default TextStyleTools;